/**
 * @fileoverview Sidebar resize handle functionality for adjustable sidebar width.
 * @module ui/SidebarResizer
 */

import { DOM_ELEMENTS, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from '../constants.js';

const { CLASS_RESIZING } = DOM_ELEMENTS;

/**
 * Handles sidebar resizing via mouse drag on the resize handle.
 * Saves width to StateManager for persistence across sessions.
 * @class
 */
export class SidebarResizer {
    /**
     * Creates a new SidebarResizer instance.
     * @param {HTMLElement} sidebar - The sidebar element to resize
     * @param {HTMLElement} resizeHandle - The drag handle element
     * @param {Object} stateManager - StateManager instance for width persistence
     * @param {Function} [onWidthChange=null] - Callback invoked when width changes
     */
    constructor(sidebar, resizeHandle, stateManager, onWidthChange = null) {
        this.sidebar = sidebar;
        this.resizeHandle = resizeHandle;
        this.stateManager = stateManager;
        this.onWidthChange = onWidthChange;
        this.isResizing = false;
        this.minWidth = SIDEBAR_MIN_WIDTH;
        this.maxWidth = SIDEBAR_MAX_WIDTH;

        this.setupEventListeners();
    }

    /**
     * Sets up mouse and touch event listeners for resize functionality.
     *
     * Two separate paths, deliberately (not pointer events): a pointer-event
     * drag dies with pointercancel the moment the browser claims the touch for
     * scrolling, which on tablets it does even through touch-action — the same
     * reason PdfViewer's swipe navigation uses touch events. Touch events keep
     * targeting the element the gesture started on for the whole drag, and a
     * non-passive preventDefault() keeps the browser from scroll-claiming it.
     * @private
     */
    setupEventListeners() {
        // Desktop mouse path
        this.resizeHandle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            this.startResize();
        });
        document.addEventListener('mousemove', (e) => {
            if (!this.isResizing) return;
            this.resizeTo(e.clientX);
        });
        document.addEventListener('mouseup', () => this.stopResize());

        // Touch path (tablets / phones)
        this.resizeHandle.addEventListener('touchstart', (e) => {
            e.preventDefault();
            this.startResize();
        }, { passive: false });
        this.resizeHandle.addEventListener('touchmove', (e) => {
            if (!this.isResizing) return;
            e.preventDefault();
            if (e.touches.length > 0) {
                this.resizeTo(e.touches[0].clientX);
            }
        }, { passive: false });
        this.resizeHandle.addEventListener('touchend', () => this.stopResize());
        this.resizeHandle.addEventListener('touchcancel', () => this.stopResize());
    }

    /**
     * Initiates the resize operation.
     * @private
     */
    startResize() {
        this.isResizing = true;
        this.resizeHandle.classList.add(CLASS_RESIZING);
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
    }

    /**
     * Applies a new sidebar width from the dragging finger/cursor position.
     * @param {number} clientX - Horizontal viewport coordinate of the drag
     * @private
     */
    resizeTo(clientX) {
        // Calculate the new width based on drag position from the right edge of viewport
        const viewportWidth = window.innerWidth;
        const newWidth = viewportWidth - clientX;

        // Clamp the width between min and max
        const clampedWidth = Math.min(Math.max(newWidth, this.minWidth), this.maxWidth);

        // Apply the new width
        this.sidebar.style.width = `${clampedWidth}px`;

        // Call the callback if sidebar is collapsed to update margin
        if (this.onWidthChange) {
            this.onWidthChange();
        }
    }

    /**
     * Ends the resize operation and saves the new width.
     * @private
     */
    stopResize() {
        if (!this.isResizing) return;

        this.isResizing = false;
        this.resizeHandle.classList.remove(CLASS_RESIZING);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        // Save the width to state
        const width = parseInt(this.sidebar.style.width);
        if (!isNaN(width)) {
            this.stateManager.setSidebarWidth(width);
        }
    }

    /**
     * Restores the previously saved sidebar width from StateManager.
     * Only applies if the saved width is within min/max bounds.
     */
    restoreWidth() {
        const savedWidth = this.stateManager.getSidebarWidth();
        if (savedWidth && savedWidth >= this.minWidth && savedWidth <= this.maxWidth) {
            this.sidebar.style.width = `${savedWidth}px`;
        }
    }
}
